// The model, over the sheet, coloured by what the field has done.
//
// The board's other view is a drawing with runs traced on it. This one
// is the model itself, laid over the same drawing, so somebody can see
// where the tracked elements actually are rather than where a tracer put
// a line. It is also the picture the exports lead with.
//
// NOTHING IN AN EXPORT SAYS WHERE A DRAWING SITS IN MODEL SPACE.
// sheetViewport exists for exactly that and no plugin build has ever
// emitted one, so the alignment is done by a person, once, by eye: scale
// the sheet, move and turn the model over it until the columns line up,
// and save. What is saved is the TRANSFORM, not a picture - saving a
// picture would freeze the model at one zoom, and the next person could
// not orbit.
//
// The model root is the same object the streaming controller measures
// distances against, so moving it moves what the loader thinks is near:
// that is intended, and it is why the transform is applied to the root
// rather than to a wrapper of our own.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, InputNumber, Segmented, Select, Space, Switch, Tooltip, message } from "antd";
import { stageContainer } from "./stagePopup";
import {
  AimOutlined, CameraOutlined, FullscreenExitOutlined, FullscreenOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Mesh, Plane, PlaneGeometry, TextureLoader, Vector3, Vector2, Box3, LinearMipmapLinearFilter, LinearFilter } from "three";
import { unionBoxForUids } from "webgl/js/viewer/frameSelection";
import { boxForUids, buildSheetMaterial, isolationFor, setSheetOpacity } from "./stageHelpers";
import { trace } from "./selectionTrace";
import WebGlApp from "webgl";
import { url } from "api";
import useSheetImage from "./useSheetImage";
import useAreasSheet, { areaSheetPages } from "./useAreasSheet";
import SheetUnderModel from "./SheetUnderModel";
import { STATUS_COLOR, STATUS_ORDER, STATUS_SHORT, STATUS_LABEL } from "./trackingTheme";
import { popupInStage } from "./stagePopup";
import { snapshotIsolation, restoreIsolation } from "./isolationSnapshot";
import { narrowedTo, asFragmentRule } from "webgl/js/loader/streaming/isolationRule";
import ViewerEffectsButton from "components/Viewer/ViewerEffectsButton";
import ViewerViewButtons from "components/Viewer/ViewerViewButtons";
import ViewerToolbar from "components/Viewer/ViewerToolbar";
import { attachBuildingGrid } from "helpers/buildingGridLoad";
import useSheetGrid from "components/Sheets/useSheetGrid";
import { composeAreaSheet } from "./areaSheetPaint";

const T = { line: "#E5E7EB", muted: "#6B7280", ink: "#111827", bg: "#FAFAFA" };

// What a fresh alignment starts from: the sheet flat, a little under the
// model, as wide as the model is. It is a starting point to drag from,
// not a guess at the right answer.
const DEFAULT_OVERLAY = {
  sheet: { scale: 100, position: { x: 0, y: 0, z: 0 }, rotation: 0, opacity: 0.85 },
  model: { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: 1 },
};

// The selection's tint and the flash a framed set gets: one blue that
// no status uses, so a selected element reads as selected whatever its
// status colour was.
const SELECT_COLOR = "#2F6FED";
const FLASH_COLOR = "#38BDF8";

const rad = (d) => (d * Math.PI) / 180;

// The legend under the canvas: one row per status in its colour, plus
// "Not tracked" in grey. Each row is a switch: off hides every element
// in that status (the grey ones, for "Not tracked"), so a site walk can
// look at what is being installed and nothing else.
const UNTRACKED_GREY = "#9AA0A6";
function StatusLegend({ touchedByUid, modelTotal, hiddenStatuses, hideUntracked, greyUntracked, selectedCount = 0, onToggleStatus, onToggleUntracked }) {
  const counts = {};
  let touched = 0;
  if (touchedByUid) touchedByUid.forEach((status) => { counts[status] = (counts[status] || 0) + 1; touched += 1; });
  const untracked = modelTotal != null ? Math.max(0, Number(modelTotal) - touched) : null;
  const fmt = (n) => (n == null ? "" : ` · ${Number(n).toLocaleString()}`);
  const row = (key, color, label, title, on, onChange, dashed) => (
    <Tooltip getPopupContainer={popupInStage} key={key} title={title}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: on ? 1 : 0.45 }}>
        <Switch size="small" checked={on} onChange={onChange} />
        <span style={{ width: 12, height: 12, borderRadius: 3, background: dashed ? "transparent" : color, border: `2px solid ${color}`, display: "inline-block" }} />
        <span style={{ fontSize: 11.5, color: T.ink, whiteSpace: "nowrap" }}>{label}</span>
      </span>
    </Tooltip>
  );
  return (
    <div style={{
      position: "absolute", left: 12, right: 12, bottom: 12, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center",
      background: "#ffffffe6", padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.line}`, pointerEvents: "auto",
    }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>STATUS</span>
      {STATUS_ORDER.map((status) => row(
        status, STATUS_COLOR[status], `${STATUS_SHORT[status]}${fmt(counts[status] || 0)}`,
        `${STATUS_LABEL[status]} - ${hiddenStatuses.has(status) ? "hidden; switch on to show" : "switch off to hide every element in this status"}`,
        !hiddenStatuses.has(status), (on) => onToggleStatus(status, on), false,
      ))}
      {row(
        "untracked", UNTRACKED_GREY, `Not tracked${fmt(untracked)}`,
        // NOT "Every fragment with no status", which is how this text
        // started. Those two words are also the LABEL of the streaming
        // switch beside it, which controls detail and no colour at all -
        // and the collision is not theoretical: within one day it made a
        // spec say "Every fragment governs grey shells" and nearly bound
        // the LOD control to the new mode pair, which would have stopped
        // distant elements loading exactly when tagging them is the
        // point. The switch keeps the name the owner knows; the prose
        // gives the words up.
        `Elements with no status${greyUntracked ? " - drawn as a grey shell until the status changes" : ""}; switch off to hide them all`,
        !hideUntracked, onToggleUntracked, true,
      )}
      {selectedCount > 0 && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.ink }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: SELECT_COLOR, display: "inline-block" }} />
          selected · {selectedCount.toLocaleString()}
        </span>
      )}
    </div>
  );
}

export default function ModelStage({
  projectId,
  project,
  sheets = [],
  sheetId,
  onSheetChange,
  documentIds = [],
  // Every element to paint, with the status to paint it: the tracked
  // scope in `projected` green, the touched ones in their own step.
  // Anything not in the map draws in the model's own material.
  statusByUid = null,
  // The ids the tree's current ticks resolve to, for the "only tracked"
  // switch, and whether that answer is still on its way.
  scopeUids = null,
  scopeResolving = false,
  // "Show only the selected elements" (owner, 2026-09-20): a VIEW state
  // owned by the board, which resolves the ticks to ids server-side and
  // refuses a row too big to isolate. Nothing here but the switch.
  onlySelected = false,
  onOnlySelected = null,
  // The viewer's own [I] isolate answers the same question as the switch
  // (root-f5, 2026-09-20). Rather than let whichever fires last win, an
  // isolation this page did not set turns the switch off.
  onIsolationTakenOver = null,
  // A big row is isolated by RULE - the fragment hashes its elements use,
  // plus the uids of merged geometry a hash cannot cover, plus the document
  // prefixes when a file filter is on (root-f5's store.setIsolationRule).
  // A million-element category has no id list; it has a rule.
  isolationRule = null,
  // THE CHECKBOXES DEFINE THE SET; CLICKS ARE ORDINARY SELECTION
  // (owner, 2026-09-20, revising "a tick IS a selection" earlier the same
  // day). Two different things with two different jobs:
  //   isolationRule  the SET - what is DRAWN in Tracked elements mode
  //   highlightUids / highlightRule  the SELECTION - what is COLOURED
  // The isolate boundary must NEVER follow the selection. That is what
  // stops a ctrl-click deselect making an object VANISH: deselecting
  // removes a highlight, not geometry, and inside the isolated set the
  // viewer behaves like any other viewer. This effect's deps are
  // deliberately free of the highlight props for that reason.
  selectionUids = null,
  autoFrame = false,
  // A filter or gate rule was run: frame EVERYTHING it matched, whether or
  // not it was already on screen ('always'), and again on every run -
  // frameKey changes each time (owner, 2026-09-21: "frame everything").
  // The Auto-frame switch still wins: off means the camera never moves.
  frameAll = false,
  frameKey = "",
  onAutoFrame = null,
  onlySelectedBusy = false,
  onlySelectedCount = 0,
  onlySelectedReady = false,
  // What a page's CUT narrows the model to. Null means everything. A
  // selection in the tree does not narrow: ticking a category is how a
  // trade says "this is what I track", not "hide the rest".
  isolateUids = null,
  // What the server said about the elevation band: a sentence when it
  // could not be used, and how many elements it added when it could.
  elevationNote = "",
  elevationAdded = 0,
  // The range the server actually searched, in feet: the levels' own
  // extent and the stretched band.
  elevationRange = null,
  // Rendered over the canvas, so it is there in full screen too. The
  // properties of whatever was clicked, with its status.
  panel = null,
  // The elements somebody has given a status (uid -> status), as opposed
  // to the whole painted scope: "Clear untracked" ghosts the rest.
  touchedByUid = null,
  // The page's selection (ctrl-clicks, ticked rows): tinted so it is
  // visible whatever its status colour.
  highlightUids = null,
  // The SAME selection when it is too big to enumerate - a whole category
  // is a few thousand fragment hashes rather than a million ids. The store
  // tests it per slot, so it costs no list and no memory; see
  // webgl/js/loader/streaming/isolationRule.js. Both are sent: a rule
  // covers a rule-shaped selection - a quantities row standing for a whole
  // category - and highlightUids covers plain clicks; they are OR-ed.
  // IT MUST NOT BE THE SET. Passing the set here paints every element in
  // it, which makes "highlight" mean nothing and hides what the person
  // actually picked.
  highlightRule = null,
  // THE QUANTITY FILTER DRIVES WHAT IS VISIBLE (owner, 2026-09-28: "filtering
  // the tracked quantities, show hide elements from 3D view as a whole").
  //   isolationRule  the SET      - what is DRAWN in Tracked elements mode
  //   highlightRule  the SELECTION - what is COLOURED
  //   hiddenRule     the FILTER    - what is HIDDEN, composed with hand-hides
  // A HIDE, not a fourth isolate: ViewerStateStore.isHidden asks the
  // individual list FIRST and the rule second, so an element somebody hid by
  // hand stays hidden whatever the filter says, and clearing the filter
  // restores exactly what they had. Two isolates would have had to fight.
  hiddenRule = null,
  // The second tab under the model column (owner, 2026-09-20): everything
  // about what is selected. Rendered by the page, shown here - this file
  // only decides which of the two the column is showing.
  details = null,
  detailsCount = null,
  // The active set's NAME, from the page. Null when no set is chosen,
  // and the tab falls back to the bare word "Selection".
  detailsLabel = null,
  // Frame the camera on these and flash them, once per focusKey: what
  // a tick in the tree or a category in "new since publish" just chose.
  focusUids = null,
  focusKey = 0,
  // The page's slice of the model. `level` names the levels to show,
  // which is exact because elements carry their own level; `elevation`
  // cuts geometrically between two heights, for the cases a level name
  // cannot express - a mezzanine, a riser between floors.
  cut = null,
  levels = [],
  onCutChange = null,
  overlay = null,
  // How many elements the model (or the picked files) holds, for the
  // legend's "Not tracked" count; null when the stats have not arrived.
  modelTotal = null,
  canEdit = false,
  onSaveOverlay,
  onPickElement,
  // BOX SELECT (owner, 2026-09-22): ctrl + drag in empty space selects every
  // element inside the box. Given, the viewer turns it on for this page only.
  onBoxSelect = null,
  // THE VIEWER'S LIVE SELECTION (Paul, 2026-09-24). Called during a drag
  // as well as on release, so the panels can follow a rectangle as it is
  // drawn: selectionChanged(state, { seq, inProgress, at }).
  onSelectionChanged = null,
  // Hide selected (the viewer strip) acts on THIS page's selection too.
  // The page's picks - a box, a tree row, a quantities row, a click - are
  // painted through highlightUids and never reach the viewer's own
  // totalSelection, so without this Hide saw nothing to hide here
  // (headless, 2026-09-22: a box of 687, totalSelection 0). onHidden lets
  // the page drop its selection the way the viewer drops its own.
  onHidden = null,
  height = "calc(70 * var(--vh))",
}) {
  // The viewer is built once; a ref keeps its callback current.
  const boxSelectRef = useRef(onBoxSelect);
  boxSelectRef.current = onBoxSelect;
  const selectionChangedRef = useRef(onSelectionChanged);
  selectionChangedRef.current = onSelectionChanged;
  const onHiddenRef = useRef(onHidden);
  onHiddenRef.current = onHidden;
  const pageSelectionRef = useRef(highlightUids);
  pageSelectionRef.current = highlightUids;
  const containerRef = useRef(null);
  // The element that goes full screen: the canvas AND its overlays, so
  // the legend and the view buttons come with it.
  const stageRef = useRef(null);
  const WebGL = useRef(null);
  const planeRef = useRef(null);
  const pollRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  // THE SHEET IS BACK IN THE MODEL TAB (owner, 2026-10-09: "restore the
  // sheet panel in construction tracking model view, and make sure we can
  // select the areas sheet, with its areas colors added to it, then we can
  // place it ... scale and align it over each level, then start selection
  // process"). It had been removed and forced off on 2026-10-02.
  //
  // Off when the tab opens, and NOT remembered: the drawing is a 4096 px
  // raster, and the old remembered key (`tracking.sheetOverlay`) is still
  // set to true for whoever had it on before 10-02 - reading it again
  // would bring a sheet back for them uninvited. A switch per visit.
  const [showSheet, setShowSheet] = useState(false);
  // The Areas tab's zones painted onto the sheet. On by default once the
  // sheet is: it is what the owner asked the sheet back FOR.
  const [showAreas, setShowAreas] = useState(true);
  // Align by two points needs the drawing on screen to pick on; it turns
  // the sheet on for this visit like the switch does.
  // "Align by 2 points" needs the sheet on the screen to pick points on
  // it, but it must not DECIDE for the person what the 3D view opens with
  // (owner, 2026-09-20: the 3D view opens without the sheet plane, and the
  // per-user memory stays). So aligning turns it on for this visit only;
  // the remembered choice is only ever changed by the switch itself.
  // Which half of the right-hand column is showing. Not remembered per
  // person on purpose: it follows what you are doing, and arriving on the
  // Sheet tab is the right default for a page about a drawing.
  // Selection is the tab the owner wants open when the tab opens.
  const [rightTab, setRightTab] = useState("selection");
  const sheetOn = showSheet;
  // A project with no published model has nothing to align. Saying so
  // beats an overlay that reads "Loading the model…" for ever, which is
  // what a missing manifest used to look like.
  const [noModel, setNoModel] = useState(false);
  const [modelBounds, setModelBounds] = useState(null);
  // The streaming session exists once the manifest has landed; the mode
  // switches below wait for it.
  const [streamingReady, setStreamingReady] = useState(false);
  // THE CONSUMER IS THE STORE, NOT THE CONTROLLER. `streamingReady` flips
  // when v.streaming.CONTROLLER appears; every effect that paints, hides,
  // isolates or highlights needs v.streaming.STATE. If the store arrives
  // after the controller - and it can, they are assigned separately -
  // those effects have already run, found no store, returned, and NOTHING
  // RE-RUNS THEM, because no dependency of theirs changed afterwards. An
  // answer in hand and nothing to hand it to (the leader's prediction,
  // 2026-09-20). So the store gets its own flag, and the effects wait for
  // the thing they actually use.
  const [storeReady, setStoreReady] = useState(false);
  // Load every fragment whatever its size on screen. On by default:
  // this is the plan the field tags the job from, and an element the
  // viewer chose not to draw cannot be tagged. Level of detail still
  // follows distance as usual. The switch is there for a machine that
  // cannot hold a large model whole.
  const [fullDetail, setFullDetail] = useState(true);
  // Show only the tracked scope. Off by default: nothing is hidden
  // unless somebody asks.
  const [onlyTracked, setOnlyTracked] = useState(false);
  // The isolation this page last asked the store for (see below).
  const ours = useRef(null);
  // Enough to enable the switch; the set itself is built only when used.
  const anyTracked = !!((scopeUids && scopeUids.length) || (touchedByUid && touchedByUid.size));
  // The sheet as a screen of dots rather than a photograph.
  const [halftoneSheet, setHalftoneSheet] = useState(false);
  // UNTRACKED IS GREY, BY DEFAULT. Every fragment of the model (or of
  // the picked files / categories) that carries no status is a grey
  // shell - all of its materials - and takes a colour only when its
  // status changes. The elements WITH a status stand in their colour.
  // The store does this without a uid list (ghostByDefault); the
  // switch below turns it off for a plain view of the model.
  const [greyUntracked, setGreyUntracked] = useState(true);
  // The legend under the canvas: a status switched off hides every
  // element in it; "Not tracked" switched off hides every grey one.
  const [hiddenStatuses, setHiddenStatuses] = useState(() => new Set());
  const [hideUntracked, setHideUntracked] = useState(false);
  const legendHiddenRef = useRef([]);
  const flashTimer = useRef(null);
  // What this stage has painted so far, so a change repaints only the
  // difference and a removed element gets its own colour back.
  const paintedRef = useRef(new Map());
  const lookedTopRef = useRef(false);

  const sheet = useMemo(
    () => sheets.find((s) => String(s._id) === String(sheetId)) || null,
    [sheets, sheetId]
  );
  // Rasterised large and crisp: 4096 px across is the biggest canvas
  // every browser will paint (Safari stops near 16 M pixels), and at
  // that size a plan's text survives zooming in. Mipmaps + anisotropy
  // below keep it sharp at the grazing angles a plan is looked at from.
  // WHICH PAGE of the document. A set is several sheets - prod BA03's
  // areas PDF is First Floor on page 1 and Second Floor on page 2 - so a
  // level's tracking page names its page, and every read below uses it.
  const sheetPage = Math.max(1, Number(overlay && overlay.sheetPage) || 1);
  const sheetImg = useSheetImage(sheetOn ? sheet : null, { scale: 3, maxWidth: 4096, quality: 0.92, page: sheetPage });
  const { areas: allAreas, here: pageAreas, img: areasImg } = useAreasSheet(projectId, sheet, sheetPage, { on: sheetOn, enabled: showAreas });
  // The page's zones painted in, when there are any and they are on. While
  // that picture is being painted nothing is drawn rather than the plain
  // drawing for a second: two textures in a row is a flash, not a sheet.
  const baseImg = showAreas && pageAreas.length ? areasImg : sheetImg;
  // THE BUILDING GRID ON THE SHEET (owner, 2026-10-24: "wire the tracking
  // Model tab sheet too"): this page's lettered and numbered lines painted
  // INTO the picture over the zones (areaSheetPaint), so they move with the
  // plane exactly - and, against the model's own G grid, show at a glance
  // whether the sheet is placed where the building is.
  const [sheetGridOn, setSheetGridOn] = useState(false);
  const sheetGrid = useSheetGrid(projectId, sheet && sheet._id, sheetPage, sheetOn && sheetGridOn);
  const [gridImg, setGridImg] = useState(null);
  useEffect(() => {
    setGridImg(null);
    if (!baseImg || !sheetGrid.lines.length) return undefined;
    let alive = true;
    composeAreaSheet(baseImg, [], { gridLines: sheetGrid.lines })
      .then((u) => { if (alive) setGridImg(u); })
      .catch(() => { /* the plain sheet stays */ });
    return () => { alive = false; };
  }, [baseImg, sheetGrid]);
  // While the grid is being painted nothing is drawn rather than the plain
  // sheet for a moment - the same rule as the zones above.
  const drawImg = sheetGrid.lines.length ? gridImg : baseImg;
  const areaPages = useMemo(() => areaSheetPages(allAreas, sheets), [allAreas, sheets]);

  // The live transform. Held in state so the controls are the source of
  // truth and the scene follows, rather than reading numbers back out of
  // three and hoping they round-trip.
  const [form, setForm] = useState(DEFAULT_OVERLAY);
  const [dirty, setDirty] = useState(false);

  // Where a fresh alignment starts.
  //
  // At the origin it was unusable: this model sits 600 ft from it, so the
  // sheet appeared somewhere off screen and the first thing a person had
  // to do was guess three numbers to bring it into view. A sheet the
  // width of the model, centred under it, is something to drag FROM.
  const startingForm = useCallback(() => {
    if (!modelBounds) return DEFAULT_OVERLAY;
    const { centre, size, min } = modelBounds;
    const width = Math.max(10, Math.round(Math.max(size.x, size.z) * 1.05));
    return {
      sheet: {
        scale: width,
        // Just under the lowest geometry, so the model reads as standing
        // on the drawing rather than buried in it.
        position: { x: +centre.x.toFixed(2), y: +(min.y - Math.max(1, size.y * 0.01)).toFixed(2), z: +centre.z.toFixed(2) },
        rotation: 0,
        opacity: 0.85,
      },
      model: { ...DEFAULT_OVERLAY.model },
    };
  }, [modelBounds]);

  // The form follows the saved page. Re-seeding on every change of
  // `startingForm` (which changes when the model's bounds arrive, a few
  // seconds after the page opens) used to throw away whatever had been
  // typed in the meantime; now a fresh page is re-seeded only while it
  // is untouched, and a saved page only when a different save arrives.
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  const overlayKey = overlay ? `${overlay._id}|${overlay.updatedAt || ""}` : "";
  useEffect(() => {
    if (!overlay) {
      if (dirtyRef.current) return;
      setForm(startingForm()); setDirty(false); return;
    }
    setForm({
      sheet: { ...DEFAULT_OVERLAY.sheet, ...(overlay.sheet || {}) },
      model: { ...DEFAULT_OVERLAY.model, ...(overlay.model || {}) },
    });
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayKey, startingForm]);

  // ---- the viewer ----
  useEffect(() => {
    if (!projectId || !containerRef.current || WebGL.current) return undefined;
    const noop = () => {};
    try {
      WebGL.current = new WebGlApp(
        containerRef.current,
        { glbs: [], manifestUrl: `${url}/uploads/${projectId}/manifest.json.gz` },
        {
          setClickedChild: noop,
          setLoading,
          setError: noop,
          setPercentsLoaded: noop,
          setAllowToResize: noop,
          setModal: noop,
          setCoordsForPopover: noop,
          // A click in the model is how the field picks the thing it is
          // about to mark installed.
          // A plain click picks one thing; a CTRL-click adds to the
          // selection so a run of fittings can be marked in one go. The
          // viewer passes the modifier as its second argument.
          findInJsonUsingViewer: (uid, keyIsCtrl) => { if (onPickElement) onPickElement(uid, !!keyIsCtrl); },
          boxSelect: (uids, info) => { if (boxSelectRef.current) boxSelectRef.current(uids, info); },
          // Fired THROUGHOUT a drag, not only at its end. The page reads
          // `meta.inProgress` and `meta.seq`; it must not treat a
          // mid-drag state as a finished one.
          selectionChanged: (state, meta) => { if (selectionChangedRef.current) selectionChangedRef.current(state, meta); },
          pageSelection: () => pageSelectionRef.current || [],
          onHidden: (uids) => { if (onHiddenRef.current) onHiddenRef.current(uids); },
          setIsPopOver: noop,
          onHoverMesh: noop,
          selectedMesh: { current: null },
          project,
        },
        null,
        false,
        false
      );
      attachBuildingGrid(WebGL.current.viewer, projectId);
      setReady(true);

      // The streamed model announces its own extent once the manifest has
      // landed. Polled rather than awaited: the loader is constructed
      // synchronously and fills this in a few ticks later, and there is
      // no callback for it.
      let tries = 0;
      let haveBounds = false;
      let haveStreaming = false;
      let haveStore = false;
      const poll = setInterval(() => {
        tries += 1;
        const props = WebGL.current && WebGL.current.loader && WebGL.current.loader.modelProps;
        const box = props && props.accumulatedBounds;
        const v = WebGL.current && WebGL.current.viewer;
        if (!haveStore && v && v.streaming && v.streaming.state) {
          haveStore = true;
          setStoreReady(true);
        }
        if (!haveStreaming && v && v.streaming && v.streaming.controller) {
          haveStreaming = true;
          setStreamingReady(true);
          // The manifest landed: there IS a model, whatever an earlier
          // tick concluded.
          setNoModel(false);
        }
        if (!haveBounds && box && !box.isEmpty()) {
          haveBounds = true;
          setNoModel(false);
          const centre = new Vector3();
          const size = new Vector3();
          box.getCenter(centre);
          box.getSize(size);
          setModelBounds({ centre, size, min: box.min.clone(), max: box.max.clone() });
          // The plan, from directly above, framing the whole model. The
          // job is read as a plan; getting there by dragging was a
          // minute of work every time the page opened.
          if (!lookedTopRef.current && v && typeof v.setStandardView === "function") {
            lookedTopRef.current = true;
            try { v.setStandardView("top"); } catch (e) { /* the view is best effort */ }
          }
        }
        // A LARGE MODEL IS NOT A MISSING ONE. This used to conclude "no
        // model" after 30 s without bounds and stop polling - SpaceX
        // takes longer than that to report them - so the notice stayed
        // up over a model that went on to load, every canvas control
        // stayed hidden behind it, and the bounds (which place the sheet
        // and enable "Fit sheet") were never picked up. Now the notice
        // waits for 90 s with NEITHER a streaming session NOR bounds, is
        // withdrawn when either arrives, and polling carries on for ten
        // minutes.
        // AND IT STOPS WHEN IT HAS THE STORE TOO, which is the thing the
        // effects actually consume. It used to stop at bounds + the
        // CONTROLLER, so a store assigned a moment later was never
        // noticed by anyone - the paint, isolate and highlight effects
        // had already run against no store and nothing would re-run them.
        // That is the race the harness saw as "hadRule true, nothing
        // painted" about two runs in five (leader, 2026-09-20).
        if (haveBounds && haveStreaming && haveStore) {
          clearInterval(poll);
        } else if (tries > 180 && !haveBounds && !haveStreaming) {
          setNoModel(true);
        }
        if (tries > 1200) clearInterval(poll);
      }, 500);
      pollRef.current = poll;
    } catch (e) {
      message.error("The model viewer could not start");
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      try {
        if (WebGL.current && typeof WebGL.current.dispose === "function") WebGL.current.dispose();
      } catch (e) { /* teardown is best effort */ }
      WebGL.current = null;
      setReady(false);
      setStoreReady(false);
    };
    // Deliberately once per project: a WebGL context plus a streaming
    // session is expensive, and re-creating it on every prop change is
    // how a page ends up with three of them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const viewer = () => WebGL.current && WebGL.current.viewer;
  const modelRoot = () => {
    const v = viewer();
    return (v && v.streaming && v.streaming.controller
      && v.streaming.controller.poolRegistry
      && v.streaming.controller.poolRegistry.sceneRoot) || null;
  };

  // DECLARED ABOVE THE EFFECT THAT CALLS IT, deliberately.
  //
  // It used to sit below, and the effect below referenced it - a const
  // read above its own declaration. It was SAFE, because an effect runs
  // after the component body, so the binding was always initialised by
  // the time the call happened. It is still the exact shape that has
  // white-paged the owner three times, and from 2026-09-20 it fails the
  // build rather than warning (no-use-before-define promoted to error).
  // Reordering costs nothing and removes the footgun; arguing that this
  // particular one was fine is how the next one gets written.
  const applySheet = useCallback((s) => {
    const mesh = planeRef.current;
    if (!mesh) return;
    const width = Math.max(1, Number(s.scale) || 100);
    const params = mesh.geometry.parameters || {};
    const aspect = params.width && params.height ? params.width / params.height : 1;
    mesh.geometry.dispose();
    mesh.geometry = new PlaneGeometry(width, width / aspect);
    mesh.position.set(Number(s.position?.x) || 0, Number(s.position?.y) || 0, Number(s.position?.z) || 0);
    // One axis only. A drawing lying at a compound angle to the model is
    // not an alignment, it is a mistake, and offering three handles
    // invites it.
    mesh.rotation.set(-Math.PI / 2, 0, rad(Number(s.rotation) || 0));
    setSheetOpacity(mesh.material, Number(s.opacity) ?? 0.85);
  }, []);

  // ---- the sheet, as a plane under the model ----
  useEffect(() => {
    const v = viewer();
    if (!v || !v.scene) return undefined;
    // Remove whatever was there: a sheet change is a different drawing,
    // not an addition.
    if (planeRef.current) {
      v.scene.remove(planeRef.current);
      const old = planeRef.current;
      if (old.geometry && old.geometry.dispose) old.geometry.dispose();
      if (old.material) {
        if (old.material.map && old.material.map.dispose) old.material.map.dispose();
        if (old.material.dispose) old.material.dispose();
      }
      planeRef.current = null;
    }
    if (!drawImg || !sheetOn) return undefined;

    let alive = true;
    new TextureLoader().load(drawImg, (texture) => {
      if (!alive) return;
      // Sharp at any distance and angle: trilinear mipmaps and the most
      // anisotropy the GPU offers (a plan is mostly seen at a slant, where
      // plain bilinear sampling smears every line into grey).
      texture.generateMipmaps = true;
      texture.minFilter = LinearMipmapLinearFilter;
      texture.magFilter = LinearFilter;
      try {
        const caps = v.renderer && v.renderer.capabilities;
        texture.anisotropy = caps && caps.getMaxAnisotropy ? caps.getMaxAnisotropy() : 8;
      } catch (e) { texture.anisotropy = 8; }
      texture.needsUpdate = true;
      const aspect = (texture.image && texture.image.width && texture.image.height)
        ? texture.image.width / texture.image.height : 1;
      const width = Math.max(1, Number(form.sheet.scale) || 100);
      // Both faces (the camera goes under a plan as often as over it), no
      // depth write (a backdrop must never occlude the model), and either
      // the drawing as it is or as a halftone screen - see stageHelpers.
      const mesh = new Mesh(
        new PlaneGeometry(width, width / aspect),
        buildSheetMaterial(texture, Number(form.sheet.opacity) ?? 0.85, halftoneSheet)
      );
      mesh.userData.texture = texture;
      mesh.name = "tracking:sheet";
      mesh.renderOrder = -1;
      mesh.rotation.x = -Math.PI / 2;
      mesh.frustumCulled = false;
      v.scene.add(mesh);
      planeRef.current = mesh;
      applySheet(form.sheet);
    }, undefined, () => { /* a sheet that will not load leaves the model alone */ });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawImg, sheetOn, ready]);

  // Swapping the sheet between plain and halftone keeps the texture and
  // the placement; only the material changes.
  useEffect(() => {
    const mesh = planeRef.current;
    if (!mesh || !mesh.userData.texture) return;
    const old = mesh.material;
    mesh.material = buildSheetMaterial(mesh.userData.texture, Number(form.sheet.opacity) ?? 0.85, halftoneSheet);
    if (old && old.dispose) old.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [halftoneSheet]);

  const applyModel = useCallback((m) => {
    const root = modelRoot();
    if (!root) return;
    root.position.set(Number(m.position?.x) || 0, Number(m.position?.y) || 0, Number(m.position?.z) || 0);
    root.rotation.set(Number(m.rotation?.x) || 0, Number(m.rotation?.y) || 0, Number(m.rotation?.z) || 0);
    const s = Number(m.scale) || 1;
    root.scale.set(s, s, s);
    // The streaming controller measures tile distances through this
    // matrix, so it has to be current before the next tick rather than
    // at the next render.
    root.updateMatrixWorld(true);
  }, []);

  useEffect(() => { applySheet(form.sheet); }, [form.sheet, applySheet]);
  // `ready` is the viewer; the model's root only exists once STREAMING
  // is up, a few ticks later - so a saved model scale / position was
  // applied to nothing on reload and looked unsaved. Re-applied when the
  // root appears.
  useEffect(() => { applyModel(form.model); }, [form.model, applyModel, ready, streamingReady]);

  // ---- the elevation cut ----
  //
  // Two horizontal planes on the renderer, which clips every material at
  // once rather than needing each to opt in. Cleared when the page is not
  // cutting, because a plane left behind would slice the next page.
  // IT REMOVES ITS OWN PLANES, NOT EVERYONE'S. This used to assign
  // `clippingPlanes = []` whenever the page was not cutting, which throws
  // away any plane the viewer's own section tool made - so a section
  // button added here later would appear to work and then be wiped the
  // next time anything re-ran this effect, and the next person would
  // debug the button (leader, 2026-09-20: fix this BEFORE anyone adds
  // interactive section, not alongside it).
  const ourPlanes = useRef([]);
  useEffect(() => {
    const v = viewer();
    if (!v || !v.renderer) return;
    // A plane lives on the RENDERER, so moving one changes the picture
    // without touching the pools - no renderSignal is raised, and the
    // page is left waiting on render-on-demand's idle net to notice.
    // The owner read that as the cut "not reflecting in real time": it
    // did move, half a second late. Whoever changes a plane asks for the
    // frame (viewer.requestRender), both when a cut is installed and
    // when it is dropped.
    const dropOurs = () => {
      if (!ourPlanes.current.length) return;
      const mine = new Set(ourPlanes.current);
      v.renderer.clippingPlanes = (v.renderer.clippingPlanes || []).filter((p) => !mine.has(p));
      ourPlanes.current = [];
      if (v.requestRender) v.requestRender(0);
    };
    const mode = cut && cut.mode;
    const bottom0 = Number(cut && cut.bottom);
    const top0 = Number(cut && cut.top);
    // A non-finite constant clips EVERYTHING, which reads as a lost
    // model rather than as a bad number, so it falls back to not cutting.
    if (mode !== "elevation" || !Number.isFinite(bottom0) || !Number.isFinite(top0)) {
      dropOurs();
      return;
    }
    const bottom = Math.min(bottom0, top0);
    const top = Math.max(bottom0, top0);
    v.renderer.localClippingEnabled = true;
    dropOurs();
    // Keep what is above the bottom, and what is below the top.
    const planes = [new Plane(new Vector3(0, 1, 0), -bottom), new Plane(new Vector3(0, -1, 0), top)];
    ourPlanes.current = planes;
    v.renderer.clippingPlanes = [...(v.renderer.clippingPlanes || []), ...planes];
    if (v.requestRender) v.requestRender(0);
  }, [cut, ready]);

  // ---- every fragment, loaded ----
  //
  // The streaming viewer skips what is not worth its pixels: a fragment
  // too small waits, a heavy one waits longer. That is right for
  // exploring and wrong for this page, whose picture is every element
  // coloured by where it stands. Level of detail still follows distance.
  // Off again when the page is left, so the model viewer is unaffected.
  useEffect(() => {
    const v = viewer();
    const streaming = v && v.streaming;
    if (!streaming || typeof streaming.setDrawEverything !== "function") return undefined;
    try { streaming.setDrawEverything(!!fullDetail); } catch (e) { /* best effort */ }
    return () => { try { streaming.setDrawEverything(false); } catch (e) { /* teardown */ } };
  }, [fullDetail, streamingReady]);

  // ---- what the FILTER hides (owner, 2026-09-28) ----
  //
  // Its own effect rather than a branch inside the isolation one: the two
  // answer different questions and a shared effect would make "why is this
  // invisible" unanswerable, which has cost whole evenings on this page.
  // The rule is built by the page (webgl/.../filterVisibility) and is null
  // whenever it must not apply - no filter, or a match too large for the
  // server to enumerate, in which case the view is left exactly as it was
  // and the page has already said so in words.
  useEffect(() => {
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    if (!store) return;
    if (hiddenRule && typeof store.setHiddenRule === "function") store.setHiddenRule(hiddenRule);
    else if (typeof store.clearHiddenRule === "function") store.clearHiddenRule();
  }, [hiddenRule, ready, streamingReady, storeReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- what the page narrows to ----
  //
  // Only a page's cut, or the "only tracked" switch, hides anything.
  // Isolation rather than hiding one by one: the store composes it with
  // hidden and highlighted state, so an element outside the cut goes
  // away without losing the status it was given.
  useEffect(() => {
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    if (!store) return;
    // "Only tracked" = the ticked scope plus everything with a status;
    // the legend's "Not tracked" off = only what has a status. See
    // stageHelpers.isolationFor.
    // A rule outranks a list: when the board has one, the legend's own
    // narrowing (only tracked / hidden statuses) does not apply - a rule is
    // "show this row", and those switches shape what is PAINTED.
    if (isolationRule && typeof store.setIsolationRule === "function") {
      ours.current = null;
      // THE PAGE'S CUT STILL APPLIES (owner, 2026-10-19): the rule says which
      // rows are ticked, the cut says which elements this page is about.
      // isolateUids is the cut's uid list (levels + what the section config
      // adds); without it a ticked category drew on every floor.
      const asRule = asFragmentRule(isolationRule);
      const narrowed = isolateUids && asRule && asRule.isFragmentIsolation
        ? narrowedTo(asRule, isolateUids)
        : isolationRule;
      store.setIsolationRule(narrowed);
      return;
    }
    const uids = isolationFor({ isolateUids, onlyTracked, scopeUids, hideUntracked, touchedByUid });
    // What WE last asked for, so an isolation from elsewhere ([I], or the
    // viewer's own selection path) can be told apart from our own.
    ours.current = uids ? new Set(uids) : null;
    if (uids) store.setIsolation(uids);
    else if (typeof store.clearIsolation === "function") store.clearIsolation();
    else store.setIsolation(null);
  }, [isolationRule, isolateUids, onlyTracked, hideUntracked, touchedByUid, scopeUids, ready, streamingReady, storeReady]);

  // [I] while "Only the selected elements" is on: the button wins and the
  // switch goes off, so the two never race through the same store.
  useEffect(() => {
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    if (!store || typeof store.subscribe !== "function" || !onIsolationTakenOver) return undefined;
    return store.subscribe((type) => {
      if (type !== "isolate" && type !== "clear-isolate") return;
      const mine = ours.current;
      const now = store.isolated || null;
      const same = (!mine && !now) || (mine && now && mine.size === now.size && [...now].every((u) => mine.has(u)));
      if (!same) onIsolationTakenOver();
    });
  }, [onIsolationTakenOver, ready, streamingReady, storeReady]);

  // ---- untracked = grey, everywhere ----
  //
  // The store ghosts every uid that carries no highlight; the status
  // paint below highlights exactly the elements that have a status, so
  // the two together are "grey until the status changes" for the whole
  // model, without ever listing the untracked. Off again when the page
  // is left, so the model viewer is unaffected.
  useEffect(() => {
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    if (!store || typeof store.setGhostByDefault !== "function") return undefined;
    try { store.setGhostByDefault(!!greyUntracked); } catch (e) { /* the model still draws */ }
    return () => { try { store.setGhostByDefault(false); } catch (e) { /* teardown */ } };
  }, [greyUntracked, ready, streamingReady, storeReady]);

  // ---- the legend's per-status switches ----
  //
  // A status switched off hides every element in it - a difference
  // against what the legend hid last time, so a status change moves one
  // element and the rest are left alone.
  useEffect(() => {
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    if (!store) return;
    const want = [];
    if (touchedByUid && hiddenStatuses.size) {
      touchedByUid.forEach((status, uid) => { if (hiddenStatuses.has(status)) want.push(uid); });
    }
    const before = legendHiddenRef.current;
    const wantSet = new Set(want);
    const beforeSet = new Set(before);
    const back = before.filter((u) => !wantSet.has(u));
    const add = want.filter((u) => !beforeSet.has(u));
    try {
      if (back.length && typeof store.bulkShow === "function") store.bulkShow(back);
      if (add.length && typeof store.bulkHide === "function") store.bulkHide(add);
    } catch (e) { /* the model still draws */ }
    legendHiddenRef.current = want;
  }, [hiddenStatuses, touchedByUid, ready, streamingReady, storeReady]);
  useEffect(() => () => {
    // Leaving the page gives the hidden ones back.
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    if (store && legendHiddenRef.current.length && typeof store.bulkShow === "function") {
      try { store.bulkShow(legendHiddenRef.current); } catch (e) { /* teardown */ }
    }
  }, []);

  // ---- status colours ----
  //
  // The model always carries STATUS colour. A tracking list has a colour
  // of its own and it is deliberately not used here: how far along the
  // work is is the question this view exists to answer.
  //
  // Painted as a DIFFERENCE against what is already painted. A status
  // change repaints one element; a scope change clears the elements that
  // left it, which the store would otherwise keep green for ever.
  useEffect(() => {
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    // THE ANSWER ARRIVING BEFORE ITS CONSUMER EXISTS (leader's prediction,
    // 2026-09-20). This effect is the consumer: if it runs while there is
    // no store it returns, and nothing re-pushes unless one of its deps
    // changes afterwards. So the case that would be invisible is exactly
    // "a rule was in hand and there was nothing to hand it to" - recorded
    // here rather than inferred, because a silent early return is how the
    // last three of these hid.
    if (!store) {
      trace('stage:no-consumer', {
        hadRule: !!highlightRule,
        ruleElements: highlightRule && highlightRule.elementCount !== undefined ? highlightRule.elementCount : null,
        listUids: highlightUids ? highlightUids.length : 0,
        ready: !!ready, streamingReady: !!streamingReady,
      });
      return;
    }
    const want = new Map();
    // With untracked greyed (the default), only the elements that HAVE a
    // status are coloured; everything else is a grey shell. With it off,
    // the scope's elements without a status wear the "projected" colour.
    const source = greyUntracked ? touchedByUid : statusByUid;
    if (source) {
      source.forEach((status, uid) => {
        const color = STATUS_COLOR[status];
        if (color) want.set(uid, color);
      });
    }
    // The selection sits on top of every status colour.
    if (highlightUids && highlightUids.length) {
      for (const uid of highlightUids) want.set(uid, SELECT_COLOR);
    }
    // ...and the rule-shaped half of the same selection, which never
    // enters this Map. That is what makes deselecting free: the status
    // colours underneath were never overwritten, so clearing the rule
    // UNCOVERS them rather than having to repaint them (the owner:
    // "selection colour is temporary - when deselected, the status colour
    // takes effect again").
    try {
      if (highlightRule && typeof store.setHighlightRule === "function") {
        store.setHighlightRule(highlightRule, SELECT_COLOR);
      } else if (typeof store.clearHighlightRule === "function") {
        store.clearHighlightRule();
      }
    } catch (e) { /* the model still draws */ }
    // WHY THE RULE IS OR IS NOT THERE - recorded, not inferred.
    //
    // The branch above is correct, and a real bug still hid behind it for
    // a day: when `highlightRule` arrives null this effect quietly
    // CLEARS, and from outside that is INDISTINGUISHABLE from the effect
    // never having run. Both look like "no rule on the store" - which is
    // exactly what a browser probe reported after a successful tick. It
    // could say the rule was missing and not say WHERE it went missing.
    //
    // So the decision is written down. It changes no behaviour and is
    // read by tools/viewer-check.py. It is deliberately NOT surfaced to
    // anyone using the page: someone ticking a category should not be
    // told about internal shapes. A condition that skips a call and tells
    // nobody is the shape of most of what broke this week.
    try {
      store.selectionDiag = {
        at: Date.now(),
        // The page's own record, carried WHERE THE HARNESS ALREADY LOOKS
        // (leader, 2026-09-20: the harness reports selectionDiag, so
        // nothing of mine reached him). Whoever reads one now reads both.
        page: typeof window !== 'undefined' && window.__trackingSelection
          ? (window.__trackingSelection.rows || []).slice(-8) : [],
        hadRule: !!highlightRule,
        ruleHashes: highlightRule && highlightRule.hashes ? highlightRule.hashes.length : 0,
        ruleSingles: highlightRule && highlightRule.singles ? highlightRule.singles.length : 0,
        ruleElements: highlightRule && highlightRule.elementCount !== undefined
          ? highlightRule.elementCount : null,
        listUids: highlightUids ? highlightUids.length : 0,
      };
    } catch (e) { /* a diagnostic must never break the paint */ }
    trace('stage:push', {
      hadRule: !!highlightRule,
      ruleElements: highlightRule && highlightRule.elementCount !== undefined ? highlightRule.elementCount : null,
      listUids: highlightUids ? highlightUids.length : 0,
      ready: !!ready, streamingReady: !!streamingReady,
      hadStore: true,
    });
    const painted = paintedRef.current;
    const gone = [];
    painted.forEach((color, uid) => { if (!want.has(uid)) gone.push(uid); });
    const changed = [];
    want.forEach((color, uid) => { if (painted.get(uid) !== color) changed.push([uid, color]); });
    try {
      if (gone.length && typeof store.bulkClearHighlights === "function") store.bulkClearHighlights(gone);
      if (changed.length) store.bulkHighlight(changed);
    } catch (e) { /* the model still draws */ }
    paintedRef.current = want;
  }, [statusByUid, touchedByUid, highlightUids, highlightRule, greyUntracked, ready, streamingReady, storeReady]);

  // ---- frame and flash what was just chosen ----
  //
  // A tick in the tree or a category in "new since publish" is a claim
  // about the model, and the model answers: the camera fits to the
  // elements and they flash for a moment, so the person sees what they
  // just said they track. Only the elements already streamed can be
  // framed; with every fragment loading, that is nearly all of them.
  const frameUids = useCallback((uids) => {
    const v = viewer();
    const c = v && v.streaming && v.streaming.controller;
    const adapter = c && c.adapter;
    if (!v || !v.cameraControls || !adapter || !adapter.activeUidOwner) return false;
    const ownerOf = (uid) => {
      const o = adapter.activeUidOwner.get(uid);
      return o && o.record ? { mesh: o.record.mesh, index: o.record.index } : null;
    };
    const box = boxForUids(ownerOf, uids);
    if (!box) return false;
    try {
      v.cameraControls.fitToBox(box, true, { paddingTop: 0.15, paddingBottom: 0.15, paddingLeft: 0.15, paddingRight: 0.15 });
    } catch (e) { return false; }
    return true;
  }, []);

  // Auto-frame: the camera grows to hold the whole selection, through
  // root-f5's one door (webgl/js/viewer/frameSelection). 'auto' moves only
  // when the selection is not already comfortably visible, so a person
  // working in one corner is not dragged about by their own ticking; the
  // switch off means 'never'. One call per SETTLED selection - the board
  // debounces the resolution - and after the isolation, so the box is what
  // is actually on screen.
  const framedRef = useRef("");
  useEffect(() => {
    const v = viewer();
    if (!v || typeof v.frameSelectionUids !== "function") return;
    const hasList = !!(selectionUids && selectionUids.length);
    if (!hasList && !highlightRule) { framedRef.current = ""; return; }
    // A rule-shaped selection has no list to key on - frame it by what it
    // stands for. Without this, "frame what I ticked" framed only the
    // `singles` and looked like it had missed most of the category.
    const key = `${frameKey}|` + (highlightRule
      ? `${autoFrame ? 1 : 0}:rule:${highlightRule.elementCount || 0}:${(highlightRule.hashes || []).length}`
      : `${autoFrame ? 1 : 0}:${selectionUids.length}:${selectionUids[0]}:${selectionUids[selectionUids.length - 1]}`);
    if (framedRef.current === key) return;
    framedRef.current = key;
    const opts = { mode: autoFrame ? (frameAll ? "always" : "auto") : "never", rule: highlightRule || null };
    const reason = v.frameSelectionUids(hasList ? selectionUids : null, opts);
    // A FILTER'S MATCH MAY NOT BE STREAMED IN YET: the box is built from
    // what is loaded, so an empty box right after a run means "not here
    // yet", not "nothing". Try again as the tiles arrive - the same
    // patience the focus path has - unless something newer was framed.
    // THE RETRIES ARE CANCELLED when the page goes or a newer framing takes
    // over (Hana, 2026-09-21): uncleared, they could fire up to 4 s after
    // someone left, and were only safe because a second guard (window.__sl
    // gone) happened to catch them. Safe by construction now.
    const retries = [];
    if (reason === "empty" && frameAll && autoFrame) {
      [1500, 4000].forEach((ms) => retries.push(setTimeout(() => {
        if (framedRef.current !== key) return;
        const w = viewer();
        if (w && typeof w.frameSelectionUids === "function") w.frameSelectionUids(hasList ? selectionUids : null, opts);
      }, ms)));
    }
    // Why it moved, when a person might otherwise call it random.
    if (reason === "behind") message.info("The camera turned to show what you selected - it was behind you");
    else if (reason === "too-small") message.info("Zoomed in: what you selected was a few pixels on screen");
    return () => { retries.forEach(clearTimeout); };
  }, [autoFrame, selectionUids, highlightRule, ready, streamingReady, frameAll, frameKey]);

  useEffect(() => {
    if (!focusKey || !focusUids || !focusUids.length) return undefined;
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    if (!store) return undefined;
    // Frame now; if nothing is on screen yet, try again shortly - the
    // tiles may still be arriving.
    if (!frameUids(focusUids)) {
      setTimeout(() => frameUids(focusUids), 1500);
    }
    // Flash, then hand the colours back to the paint effect.
    const uids = focusUids.slice(0, 60000);
    try { store.bulkHighlight(uids.map((u) => [u, FLASH_COLOR])); } catch (e) { /* best effort */ }
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => {
      const painted = paintedRef.current;
      const restore = [];
      const clear = [];
      for (const u of uids) {
        const c = painted.get(u);
        if (c) restore.push([u, c]); else clear.push(u);
      }
      try {
        if (restore.length) store.bulkHighlight(restore);
        if (clear.length && typeof store.bulkClearHighlights === "function") store.bulkClearHighlights(clear);
      } catch (e) { /* best effort */ }
    }, 2500);
    return () => { if (flashTimer.current) clearTimeout(flashTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  // ---- what must stay on screen ----
  //
  // The streaming loader skips a fragment too small to be worth its
  // pixels, which is right for exploring a model and wrong for this
  // view: reading the state of a job means standing back far enough to
  // see all of it, and at that distance a hanger or a fitting is under
  // the floor. Everything this page has coloured, and anything isolated
  // for placing, is pinned so the picture is honest at every distance.
  //
  // Moot while the whole model is drawn; it is what keeps the coloured
  // elements on screen when somebody switches full detail off.
  useEffect(() => {
    const v = viewer();
    const streaming = v && v.streaming;
    if (!streaming || typeof streaming.setPinnedUids !== "function") return;
    if (fullDetail) { try { streaming.setPinnedUids([]); } catch (e) { /* best effort */ } return; }
    const uids = new Set();
    if (statusByUid) statusByUid.forEach((_status, uid) => uids.add(uid));
    if (isolateUids) for (const uid of isolateUids) uids.add(uid);
    try { streaming.setPinnedUids([...uids]); } catch (e) { /* best effort */ }
  }, [statusByUid, isolateUids, fullDetail, ready, streamingReady]);

  // One alignment edit: the form changes, the scene follows, Save writes it.
  const set = (part, patch) => {
    setForm((f) => ({ ...f, [part]: { ...f[part], ...patch } }));
    setDirty(true);
  };

  // ---- the picture the exports lead with ----
  //
  // Captured from the canvas the user is looking at, at a higher pixel
  // ratio for one frame. Rendering it again server-side would need a GPU
  // this box does not have, and would be a second implementation of the
  // viewer whose only job is to disagree with this one.
  const capture = useCallback((factor = 2) => {
    const v = viewer();
    if (!v || !v.renderer) return null;
    const renderer = v.renderer;
    const before = renderer.getPixelRatio();
    try {
      renderer.setPixelRatio(Math.min(4, before * factor));
      if (v.composer && typeof v.composer.setPixelRatio === "function") {
        v.composer.setPixelRatio(Math.min(4, before * factor));
      }
      // One place decides how a frame is drawn (viewer.drawFrame).
      if (v.drawFrame) v.drawFrame();
      else if (v.composer) v.composer.render();
      else renderer.render(v.scene, v.camera);
      return renderer.domElement.toDataURL("image/png");
    } catch (e) {
      return null;
    } finally {
      renderer.setPixelRatio(before);
      if (v.composer && typeof v.composer.setPixelRatio === "function") v.composer.setPixelRatio(before);
      try { if (v.drawFrame) v.drawFrame(); else if (v.composer) v.composer.render(); else renderer.render(v.scene, v.camera); } catch (e) { /* the next frame will */ }
    }
  }, []);

  // The plan: the same canvas from directly above, framing the whole
  // model, with every status colour in place - captured without
  // animation and without moving the person's own view. The camera is
  // saved, turned to the top view, fitted, rendered once, captured, and
  // put back; the next frame the user sees is the one they had.
  // `size` renders OFF-SCREEN at that many pixels (owner, 2026-09-22: "make
  // the layout as big as possible on 1 page to show all details"): the
  // export's plans used to be the on-screen canvas x1.5, ~1150 px across,
  // stretched onto an A3 sheet. `fitUids` frames those elements (a page's
  // levels) instead of the whole building, so a level fills its sheet.
  // Both are put back before the next frame the person sees.
  const captureTopView = useCallback((factor = 2, { size = null, fitUids = null } = {}) => {
    const v = viewer();
    if (!v || !v.renderer || !v.cameraControls || !modelBounds) return null;
    const cc = v.cameraControls;
    const renderer = v.renderer;
    const before = renderer.getPixelRatio();
    const prevSize = new Vector2();
    renderer.getSize(prevSize);
    const cam = v.camera;
    const prevAspect = cam && cam.aspect;
    let resized = false;
    let saved = false;
    try {
      cc.saveState();
      saved = true;
      if (size && size.width && size.height) {
        renderer.setSize(size.width, size.height, false);
        if (v.composer && typeof v.composer.setSize === "function") v.composer.setSize(size.width, size.height);
        if (cam && cam.isPerspectiveCamera) { cam.aspect = size.width / size.height; cam.updateProjectionMatrix(); }
        resized = true;
      }
      let box = new Box3(modelBounds.min.clone(), modelBounds.max.clone());
      if (fitUids && fitUids.length) {
        const c = v.streaming && v.streaming.controller;
        const fb = c && c.adapter ? unionBoxForUids(c.adapter, fitUids) : null;
        if (fb && !fb.isEmpty()) box = fb;
      }
      const angles = (v.constructor && v.constructor.STANDARD_VIEWS && v.constructor.STANDARD_VIEWS.top) || [0, 0.0001];
      cc.rotateTo(angles[0], angles[1], false);
      cc.fitToBox(box, false, { paddingTop: 0.02, paddingBottom: 0.02, paddingLeft: 0.02, paddingRight: 0.02 });
      cc.update(0);
      // Off-screen at a set size the pixels ARE the size; on screen, a factor.
      const ratio = resized ? 1 : Math.min(4, before * factor);
      renderer.setPixelRatio(ratio);
      if (v.composer && typeof v.composer.setPixelRatio === "function") v.composer.setPixelRatio(ratio);
      if (v.drawFrame) v.drawFrame(); else if (v.composer) v.composer.render(); else renderer.render(v.scene, v.camera);
      return renderer.domElement.toDataURL("image/png");
    } catch (e) {
      return null;
    } finally {
      if (resized) {
        renderer.setSize(prevSize.x, prevSize.y, false);
        if (v.composer && typeof v.composer.setSize === "function") v.composer.setSize(prevSize.x, prevSize.y);
        if (cam && cam.isPerspectiveCamera && prevAspect) { cam.aspect = prevAspect; cam.updateProjectionMatrix(); }
      }
      renderer.setPixelRatio(before);
      if (v.composer && typeof v.composer.setPixelRatio === "function") v.composer.setPixelRatio(before);
      if (saved) { try { cc.reset(false); cc.update(0); } catch (e) { /* the view stays where the capture left it */ } }
      try { if (v.drawFrame) v.drawFrame(); else if (v.composer) v.composer.render(); else renderer.render(v.scene, v.camera); } catch (e) { /* the next frame will */ }
    }
  }, [modelBounds]);

  // A layout for the export (owner, 2026-09-18): every element without a
  // status as the grey halftone, whatever the switch says on screen, and
  // optionally only one level (`isolate`: its uids) - for one frame, then
  // the person's own isolation and grey switch are put back.
  const captureLayout = useCallback(({ isolate = null, top = true, factor = 1.5, size = null } = {}) => {
    const v = viewer();
    const store = v && v.streaming && v.streaming.state;
    // SNAPSHOT INSIDE THE TRY. This read used to be `[...store.isolated]`
    // above the try: an isolation can be a RULE, a rule is not iterable,
    // so the spread threw before any restore existed and captureLayouts'
    // catch turned it into an export with NO pages - for exactly the
    // people who had isolated a big row first (owner, 2026-09-28: the
    // level pages have to be accurate).
    let snap = null;
    const prevGhost = store && typeof store.isGhostByDefault === "function" ? store.isGhostByDefault() : null;
    try {
      snap = snapshotIsolation(store);
      if (store && typeof store.setGhostByDefault === "function") store.setGhostByDefault(true);
      if (store && isolate) store.setIsolation(isolate);
      return top ? captureTopView(factor, { size, fitUids: isolate }) : capture(factor);
    } finally {
      if (store && isolate && snap) { try { restoreIsolation(store, snap); } catch (e) { /* the next isolation effect restores it */ } }
      if (store && prevGhost !== null) { try { store.setGhostByDefault(prevGhost); } catch (e) { /* the grey effect restores it */ } }
    }
  }, [capture, captureTopView]);

  // Handed up so the page's export buttons can take the picture without
  // reaching into this component's internals.
  useEffect(() => {
    if (!onSaveOverlay) return undefined;
    onSaveOverlay.captureRef = capture;
    onSaveOverlay.captureTopViewRef = captureTopView;
    onSaveOverlay.captureLayoutRef = captureLayout;
    return () => { if (onSaveOverlay) { onSaveOverlay.captureRef = null; onSaveOverlay.captureTopViewRef = null; onSaveOverlay.captureLayoutRef = null; } };
  }, [onSaveOverlay, capture, captureTopView, captureLayout]);

  const save = async () => {
    if (!onSaveOverlay || !onSaveOverlay.save) return;
    const v = viewer();
    const cam = v && v.camera;
    const target = v && v.cameraControls && v.cameraControls.getTarget
      ? (() => { const t = { x: 0, y: 0, z: 0 }; try { const o = v.cameraControls.getTarget(); Object.assign(t, { x: o.x, y: o.y, z: o.z }); } catch (e) { /* default */ } return t; })()
      : { x: 0, y: 0, z: 0 };
    await onSaveOverlay.save({
      sheetId, page: sheetPage,
      sheet: form.sheet,
      model: form.model,
      camera: {
        position: cam ? { x: cam.position.x, y: cam.position.y, z: cam.position.z } : { x: 0, y: 0, z: 0 },
        target,
      },
      documentIds,
    });
    setDirty(false);
  };
  // The same body to every page (ModelTracking saveAll keeps each page's
  // own levels, sheet height and camera).
  const saveAll = async () => {
    if (!onSaveOverlay || !onSaveOverlay.saveAll) return;
    await onSaveOverlay.saveAll({ sheetId, page: sheetPage, sheet: form.sheet, model: form.model, documentIds });
    setDirty(false);
  };

  const frame = () => {
    const v = viewer();
    try {
      if (v && typeof v.targetItems === "function") v.targetItems();
    } catch (e) { /* framing is best effort */ }
  };

  const fitAll = () => {
    const v = viewer();
    try {
      if (v && typeof v.fitToModel === "function") v.fitToModel();
      else frame();
    } catch (e) { /* best effort */ }
  };

  // Full screen.
  //
  // A model over a drawing is a picture you read, and half a screen of
  // it with a properties panel beside it is not enough to read a floor
  // by. The browser's own full screen is used rather than a CSS overlay
  // so the canvas really does get every pixel; the viewer is told to
  // resize because a canvas sized to its old container stays that size
  // inside the new one.
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const onChange = () => {
      const full = !!document.fullscreenElement;
      setIsFull(full);
      // A TOAST HAS TO FOLLOW THE TOP LAYER, NOT JUST THE MODALS.
      //
      // Owner, 2026-09-30: he pressed a bulk status change in full screen and
      // sat on a spinner. The confirm was the cause (see stagePopup), but the
      // success toast and its Undo link would have been invisible too - every
      // message.* in these pages renders through ONE global holder on
      // document.body. antd recomputes that holder container on re-render and
      // config() triggers the re-render, so re-stating it on each transition is
      // what actually moves it; setting it once at mount would not.
      try { message.config({ getContainer: stageContainer }); } catch (e) { /* toasts stay where they are */ }
      // After the browser has finished the transition, not during it.
      window.setTimeout(() => {
        const v = viewer();
        try { if (v && typeof v.onWindowResize === "function") v.onWindowResize(); } catch (e) { /* best effort */ }
      }, 120);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFull = () => {
    const el = stageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      else if (el.requestFullscreen) el.requestFullscreen();
      else message.info("This browser will not let a page go full screen");
    } catch (e) { message.error("Could not go full screen"); }
  };

  const numberRow = (label, value, onChange, step = 1) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span style={{ width: 74, fontSize: 12, color: T.muted }}>{label}</span>
      <InputNumber size="small" value={value} step={step} onChange={onChange} style={{ width: 110 }} disabled={!canEdit} />
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 12, height }}>
      <div
        ref={stageRef}
        // FULL SCREEN PUTS THIS ELEMENT IN THE TOP LAYER AND RENDERS IT ALONE,
        // so every antd popup inside it must anchor here instead of on
        // document.body - see stagePopup.js. The attribute is the anchor.
        data-stage-root=""
        style={{
          flex: 1, minWidth: 0, position: "relative", border: `1px solid ${T.line}`,
          borderRadius: isFull ? 0 : 6, overflow: "hidden", background: T.bg,
        }}
      >
        <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
        {noModel ? (
          <div style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 8, textAlign: "center", padding: 24,
          }}>
            <div style={{ fontWeight: 600, color: T.ink }}>No model to align</div>
            <div style={{ fontSize: 12.5, color: T.muted, maxWidth: 380 }}>
              This project has no published model, so there is nothing to place over a sheet.
              Publish one from the Revit plugin, then come back.
            </div>
          </div>
        ) : loading && (
          <div style={{ position: "absolute", left: 12, bottom: 12, fontSize: 12, color: T.muted, background: "#ffffffcc", padding: "4px 8px", borderRadius: 4 }}>
            Loading the model…
          </div>
        )}
        {!noModel && (
          <StatusLegend
            touchedByUid={touchedByUid}
            modelTotal={modelTotal}
            hiddenStatuses={hiddenStatuses}
            hideUntracked={hideUntracked}
            greyUntracked={greyUntracked}
            selectedCount={highlightUids ? highlightUids.length : 0}
            onToggleStatus={(status, on) => setHiddenStatuses((prev) => {
              const next = new Set(prev);
              if (on) next.delete(status); else next.add(status);
              return next;
            })}
            onToggleUntracked={(on) => setHideUntracked(!on)}
          />
        )}
        {/* THE MODEL VIEW'S TOOLBAR, AND IT NEEDS ITS OWN PLACE ON THIS PAGE.
            It positions itself `position-absolute` with the `viewer-zoom`
            class, and that class only carries coordinates in the
            model-view / timeline stylesheet - so here it fell back to its
            static position inside the top-right control column and measured
            x=-41, 764 wide, lying across twenty controls: the whole views
            strip and every switch (headless, 2026-09-23). A wrapper that IS
            positioned gives it a corner of its own, above the status
            legend and clear of both rows. */}
        {/* AND THE WRAPPER NEEDS WIDTH. The group inside is absolute with
            `right: 0`, so against a zero-width wrapper it aligned its RIGHT
            edge to the wrapper's LEFT edge and ran off-screen: measured
            x=-327, six of the twelve buttons at negative x. Spanning the
            pane gives `right: 0` something real to sit against. */}
        {!noModel && (
          <div style={{ position: "absolute", left: 12, right: 12, bottom: 54, height: 0, zIndex: 5 }}>
            <ViewerToolbar viewer={() => WebGL.current && WebGL.current.viewer} gear={false} />
          </div>
        )}
        {/* The tool strip and the switch row are ONE column, so the switches
            always sit under the strip at whatever height it wraps to. They
            were two boxes pinned at top 12 and top 48: once the strip took
            its full tool set (hide, unhide) it wrapped to a second line in
            this narrow panel, and the switches sat ON Isolate, Hide and
            Unhide all - visible, and unclickable. */}
        {!noModel && (
          <div style={{
            position: "absolute", right: 12, top: 12, maxWidth: "calc(100% - 24px)",
            display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6,
            pointerEvents: "none",
          }}>
          <div style={{
            display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 4, pointerEvents: "auto",
            background: "#ffffffe6", padding: 4, borderRadius: 6, border: `1px solid ${T.line}`,
          }}>
            {/* This page's own six view buttons were here. They were
                stateless - look(name) is setStandardView(name), exactly
                what the strip calls - so they were duplication of
                APPEARANCE, not of behaviour, and the strip also offers
                Bottom, which this row never had. Everything below stays:
                fitAll, full screen, the persisted cut and the two
                switches all carry state the strip cannot express. */}
            <Tooltip getPopupContainer={popupInStage} title="Frame the whole model">
              <Button size="small" icon={<AimOutlined />} onClick={fitAll} />
            </Tooltip>
            <Tooltip getPopupContainer={popupInStage} title={isFull ? "Leave full screen" : "Full screen"}>
              <Button
                size="small"
                icon={isFull ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={toggleFull}
              />
            </Tooltip>
            {/* ALL the strip's tools too (owner, 2026-09-22: "every view we
                need full navigation"). They were off here because this page
                owned isolate and the cut; both now coexist with the viewer's
                own - the cut keeps the section tool's planes, and an [I]
                isolate hands the set switch over (onIsolationTakenOver) - and
                hide by hand outranks the legend's status hiding in the store. */}
            <ViewerViewButtons viewer={() => WebGL.current && WebGL.current.viewer} variant="floating" />
            <ViewerEffectsButton viewer={() => WebGL.current && WebGL.current.viewer} variant="floating" style={{ width: 24, height: 24, borderRadius: 4 }} />
          </div>
          <div style={{
            display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 12, alignItems: "center", pointerEvents: "auto",
            background: "#ffffffe6", padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.line}`, fontSize: 11.5,
          }}>
            <Tooltip getPopupContainer={popupInStage} title="Load every fragment whatever its size on screen, so every element can be tagged. Detail still follows distance. Switch off if this machine struggles with a large model.">
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Switch size="small" checked={fullDetail} onChange={setFullDetail} />
                Every fragment
              </span>
            </Tooltip>
            <Tooltip getPopupContainer={popupInStage} title="Draw the sheet as a screen of dots, like a print, so the model's colours stand off it">
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Switch size="small" checked={halftoneSheet} onChange={setHalftoneSheet} />
                Halftone sheet
              </span>
            </Tooltip>
            {/* SHOW UNTRACKED - renamed from "Grey out untracked" (owner,
                2026-09-20), and deliberately NOT the same control as
                "Every fragment" beside it, which is the streaming detail
                switch and governs no colour at all.
                It works in BOTH modes: in Full model the whole model's
                unstatused elements are grey shells, and in Tracked
                elements the SET'S OWN unstatused are - because the
                isolate decides what is DRAWN and this decides how the
                untouched part of it LOOKS. No enumeration either way: the
                ghost is a per-slot rule (InstanceStateApplicator:164),
                which is why it survives a selection standing for a
                million elements. */}
            <Tooltip getPopupContainer={popupInStage} title="Elements with no status are drawn as grey shells, so you can see what is left to do without losing the ones that are done. In Tracked elements mode this greys the untouched part of your selection. Off for the model's own colours.">
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Switch size="small" checked={greyUntracked} onChange={setGreyUntracked} />
                Show untracked
              </span>
            </Tooltip>
            {onAutoFrame ? (
              <Tooltip getPopupContainer={popupInStage} title="Move the camera to hold everything selected as the selection grows. It stays put when what you select is already on screen.">
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Switch size="small" checked={autoFrame} onChange={onAutoFrame} />
                  Auto-frame
                </span>
              </Tooltip>
            ) : null}
            {onOnlySelected ? (
              // TWO MODES, MUTUALLY EXCLUSIVE, NEVER BOTH (owner,
              // 2026-09-20). It was a switch called "Only the selected",
              // which reads as a modifier on top of something else; these
              // are two ways of looking at the model and one is always
              // true. A Segmented says that and a switch cannot.
              //
              // It drives the SAME props the switch did (onlySelected /
              // onOnlySelected), so the page side is unchanged - the
              // mode pair is a presentation of the isolate, not a new
              // piece of state to keep in step with it.
              <Tooltip getPopupContainer={popupInStage} title={
                onlySelectedBusy ? "Working out which elements are tracked…"
                  : onlySelectedReady
                    ? `Tracked elements: show only the ${onlySelectedCount.toLocaleString()} element${onlySelectedCount === 1 ? "" : "s"} you have ticked, and hide the rest`
                    : "Tracked elements shows only what you tick in the Model elements panel. Nothing is ticked yet, so it would show nothing."
              }>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <Segmented
                    size="small"
                    value={onlySelected ? "tracked" : "full"}
                    onChange={(v) => onOnlySelected(v === "tracked")}
                    disabled={onlySelectedBusy}
                    options={[
                      { label: "Full model", value: "full" },
                      { label: "Tracked elements", value: "tracked" },
                    ]}
                  />
                </span>
              </Tooltip>
            ) : null}
            <Tooltip getPopupContainer={popupInStage} title={
              scopeResolving ? "Working out the ticked elements…"
                : anyTracked
                  ? "Show only the ticked elements and every element with a status"
                  : "Tick what to track in the tree first"
            }>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Switch
                  size="small"
                  checked={onlyTracked}
                  loading={onlyTracked && scopeResolving}
                  disabled={!onlyTracked && !scopeResolving && !anyTracked}
                  onChange={setOnlyTracked}
                />
                Only tracked
              </span>
            </Tooltip>
          </div>
          </div>
        )}
        {/* THE VIEWER'S SCREENSHOT WRITES INTO THIS ANCHOR and finds it by
            id. Only the model viewer rendered one, so pressing S on this
            page threw - for everyone who ever pressed it - inside the
            viewer's own key handler. One hidden anchor is the whole fix,
            and it makes S do here what it does there rather than nothing
            (leader, 2026-09-20: do not leave a key that throws). */}
        {/* Not a link: a download target the viewer fills in. It carries no
            href until the screenshot sets one. */}
        {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
        <a id="take-snapshot" download="model.png" style={{ display: "none" }} aria-hidden="true" tabIndex={-1}>{" "}</a>
        {panel && (
          <div style={{
            position: "absolute", left: 12, top: 12, width: 316, maxWidth: "calc(100% - 24px)",
            maxHeight: "calc(100% - 24px)", overflowY: "auto",
            background: "#fffffff2", border: `1px solid ${T.line}`, borderRadius: 6,
            boxShadow: "0 2px 8px rgba(0,0,0,.08)",
          }}>
            {panel}
          </div>
        )}
      </div>

      <div style={{ width: 268, flexShrink: 0, overflowY: "auto", padding: "0 2px" }}>
        {/* TWO TABS, TAKING TURNS (owner, 2026-09-20). The column is 268 px
            and both halves are columns of their own, so they share it by
            turns rather than fight over it. The label carries the count
            when there is a selection, which is what makes it worth a
            click; with nothing selected the tab still opens and says what
            to do. */}
        {details ? (
          <Segmented
            size="small"
            block
            style={{ marginBottom: 8 }}
            value={rightTab}
            onChange={setRightTab}
            options={[
              // SELECTION FIRST and selected by default (owner, 2026-09-20).
              //
              // The label was "Selection · 1,19000" - a name and a count
              // welded into one string with a separator, which is why it
              // read as a broken number rather than as two facts. It is
              // now the SET'S NAME with its element count beneath, and
              // the bare word "Selection" when no set is active.
              // `detailsLabel` comes from the page (root-8e); this file
              // only renders it.
              {
                label: detailsLabel ? (
                  <span style={{ display: "block", lineHeight: 1.15, padding: "1px 0" }}>
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {detailsLabel}
                    </span>
                    {detailsCount ? (
                      <span style={{ display: "block", fontSize: 10, opacity: 0.65 }}>
                        {`${Number(detailsCount).toLocaleString()} elements`}
                      </span>
                    ) : null}
                  </span>
                ) : "Selection",
                value: "selection",
              },
              { label: "View Config", value: "sheet" },
            ]}
          />
        ) : null}
        {details && rightTab === "selection" ? details : (<>
        <SheetUnderModel
          projectId={projectId} sheets={sheets} sheetId={sheetId} sheet={sheet} sheetPage={sheetPage}
          onSheetChange={onSheetChange} canEdit={canEdit}
          areaPages={areaPages} pageAreas={pageAreas}
          sheetOn={sheetOn} onShowSheet={setShowSheet} showAreas={showAreas} onShowAreas={setShowAreas}
          sheetGridOn={sheetGridOn} onSheetGrid={setSheetGridOn} sheetGrid={sheetGrid}
          form={form} onSet={set}
          drawImg={drawImg} viewer={viewer}
          canFit={!!modelBounds && canEdit} dirty={dirty}
          onApplyAlign={(r) => {
            setForm((f) => ({ ...f, sheet: { ...f.sheet, scale: r.scale, rotation: r.rotation, position: { ...(f.sheet.position || {}), x: r.position.x, z: r.position.z } } }));
            setDirty(true);
          }}
          onFit={() => { setForm(startingForm()); setDirty(true); }}
          onUndo={() => { setForm(overlay ? { sheet: { ...DEFAULT_OVERLAY.sheet, ...overlay.sheet }, model: { ...DEFAULT_OVERLAY.model, ...overlay.model } } : DEFAULT_OVERLAY); setDirty(false); }}
        />
        <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, margin: "10px 0 6px" }}>This page shows</div>
        <Select getPopupContainer={popupInStage}
          size="small"
          style={{ width: "100%", marginBottom: 6 }}
          value={(cut && cut.mode) || "none"}
          onChange={(mode) => { if (onCutChange) onCutChange({ ...(cut || {}), mode }); }}
          disabled={!canEdit || !onCutChange}
          options={[
            { value: "none", label: "The whole model" },
            { value: "level", label: "Chosen levels" },
            { value: "elevation", label: "A section between two heights" },
          ]}
        />
        {cut && cut.mode === "level" && (
          <Select getPopupContainer={popupInStage}
            mode="multiple"
            size="small"
            style={{ width: "100%", marginBottom: 8 }}
            placeholder="Pick the levels"
            value={cut.levelNames || []}
            onChange={(levelNames) => { if (onCutChange) onCutChange({ ...cut, levelNames }); }}
            disabled={!canEdit}
            maxTagCount="responsive"
            optionFilterProp="label"
            options={levels.map((l) => ({ value: l.name, label: `${l.name} · ${l.count.toLocaleString()}` }))}
          />
        )}
        {/* AT THIS ELEVATION, WHATEVER LEVEL IT SAYS (owner, 2026-10-02:
            "allow to show elements, that are at the current elevation, but
            not tagged the current level, by compairing level1 elevation,
            with otehr elements elevation, make it a checkbox, with
            configuirable elevation").
            A level cut is a cut by the level STRING a modeller typed, and
            that is how 10,604 hangers tagged "L01" went missing from a
            "Level 1" page. The names can be aliased; an element genuinely
            tagged Level 2 that hangs in the Level 1 ceiling cannot - it is
            on the floor the field is standing on, and only its height says
            so. The server answers with the band it used and how many it
            added, so this is never a switch that silently does nothing. */}
        {cut && cut.mode === "level" && (cut.levelNames || []).length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Switch size="small" checked={!!cut.atElevation} disabled={!canEdit}
              onChange={(on) => onCutChange && onCutChange({ ...cut, atElevation: on })} />
            <span style={{ fontSize: 12, marginLeft: 8, color: T.ink }}>Also show other levels at this elevation</span>
            {/* THE SECOND SWITCH, AND IT IS NOT THE SAME QUESTION (owner,
                via the leader, 2026-10-02: "another check box, to show
                fragments lacking level tag, at that elevation range").
                The one above finds elements tagged to a DIFFERENT level;
                this one finds elements tagged to NO level. All 5,557
                Generic Models on BA03 carry an empty level, so no amount
                of name aliasing will ever reach them - they are absent
                from every level cut, and this is the only control that
                brings them back. */}
            <div style={{ marginTop: 6 }}>
              <Switch size="small" checked={!!cut.untaggedAtElevation} disabled={!canEdit}
                onChange={(on) => onCutChange && onCutChange({ ...cut, untaggedAtElevation: on })} />
              <span style={{ fontSize: 12, marginLeft: 8, color: T.ink }}>Also show untagged elements at this elevation</span>
            </div>
            {/* SAID BEFORE IT HAPPENS, NOT AFTER (owner's rule, 2026-10-02:
                "Switching on clears the level filter"). He chose this over
                the alternatives knowing it discards a filter he typed, so
                it is his decision - but a page that drops something a
                person set by hand without saying so is the shape we have
                fixed four times today. The toast at the moment of the
                change is in ModelTracking; this is the standing warning,
                which does not scroll away.
                AND IT SAYS THE FILTER DOES NOT COME BACK, because
                "clears" that secretly means "parks" is hidden state. */}
            <div style={{ fontSize: 11, color: T.muted, marginTop: 6, lineHeight: 1.45 }}>
              Switching either of these on clears this page&apos;s level filter - what stands at an elevation
              and what is named a level are two different questions, and the filter would throw out the
              elements the switch let in. It is not put back when you switch them off.
            </div>
            {(cut.atElevation || cut.untaggedAtElevation) && (
              <>
                {/* A RANGE, NOT A HEIGHT (owner, 2026-10-02: "make sure it's
                    a range not a set value"), and TWO numbers rather than
                    one: a hanger hangs below the slab it is fixed to, a
                    riser pokes up through it, and a single symmetric reach
                    makes you overshoot one end to catch the other.
                    The range itself starts from the LEVEL, floor to
                    whatever stands on it, measured from its own elements -
                    not from a slab thickness somebody typed. These two
                    stretch it. One pair serves both switches, because the
                    reach is a property of the band and not of which
                    population was asked for. */}
                {numberRow("Below (ft)", cut.elevBelow == null ? 2 : cut.elevBelow,
                  (v) => onCutChange && onCutChange({ ...cut, elevBelow: v }), 0.5)}
                {numberRow("Above (ft)", cut.elevAbove == null ? 2 : cut.elevAbove,
                  (v) => onCutChange && onCutChange({ ...cut, elevAbove: v }), 0.5)}
                <div style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.4 }}>
                  {/* THE SERVER'S SENTENCE WINS. It now says how many
                      elements were placed from the model, how many
                      borrowed a height from their level, and how many
                      could not be placed at all - which is what the old
                      "Nothing else meets that range" denied existed.
                      Only 12.23% of elements on BA03 have a position of
                      their own, so this is the majority case and not a
                      footnote. */}
                  {elevationNote || (
                    elevationRange
                      // THE RANGE IT USED, in the panel, in feet. A person
                      // can see whether it is the band they meant before
                      // deciding the switch did not work.
                      ? <>
                          These levels stand from <b>{elevationRange.levelLo}</b> to <b>{elevationRange.levelHi}</b> ft;
                          {" "}searching <b>{elevationRange.lo}</b> to <b>{elevationRange.hi}</b> ft.
                          {elevationAdded
                            ? ` ${elevationAdded.toLocaleString()} element${elevationAdded === 1 ? "" : "s"} added.`
                            // NOT "nothing else meets that range" - that
                            // asserted absence, and on this model the
                            // truth was usually that we could not place
                            // them. The server says which when it knows;
                            // this is the remaining case, where every
                            // candidate WAS placed and genuinely none of
                            // them meets the range.
                            : " Nothing else that we could place meets that range."}
                        </>
                      : "Takes the height these levels stand in, stretches it by the two reaches, and adds every element that meets that range."
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {cut && cut.mode === "elevation" && (
          <>
            {numberRow("Bottom", cut.bottom, (v) => onCutChange && onCutChange({ ...cut, bottom: v }))}
            {numberRow("Top", cut.top, (v) => onCutChange && onCutChange({ ...cut, top: v }))}
            {/* NUDGE BOTH PLANES TOGETHER (owner, 2026-10-02: "add some ui
                to manipulate the cut plane a bit above or below"). Typing
                two numbers to move a section up a foot is two chances to
                get it wrong and loses the thickness; this keeps the band
                and slides it. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, marginTop: 2 }}>
              <span style={{ width: 74, fontSize: 12, color: T.muted }}>Move band</span>
              <Button size="small" disabled={!canEdit || cut.bottom === null || cut.top === null}
                onClick={() => onCutChange && onCutChange({
                  ...cut, bottom: Number(cut.bottom) - Number(cut.nudgeStep || 1),
                  top: Number(cut.top) - Number(cut.nudgeStep || 1),
                })}>
                Down
              </Button>
              <Button size="small" disabled={!canEdit || cut.bottom === null || cut.top === null}
                onClick={() => onCutChange && onCutChange({
                  ...cut, bottom: Number(cut.bottom) + Number(cut.nudgeStep || 1),
                  top: Number(cut.top) + Number(cut.nudgeStep || 1),
                })}>
                Up
              </Button>
            </div>
            {/* HOW FAR A NUDGE IS, because "a bit" is a different amount in
                a riser than on a floor plate. */}
            {numberRow("Step", cut.nudgeStep == null ? 1 : cut.nudgeStep,
              (v) => onCutChange && onCutChange({ ...cut, nudgeStep: v }), 0.5)}
            <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8, lineHeight: 1.4 }}>
              The band keeps its thickness; Up and Down slide both planes by the step.
            </div>
          </>
        )}

        <Space direction="vertical" style={{ width: "100%", marginTop: 12 }}>
          <Space>
            <Tooltip getPopupContainer={popupInStage} title="Frame the model">
              <Button size="small" icon={<AimOutlined />} onClick={frame} />
            </Tooltip>
            <Tooltip getPopupContainer={popupInStage} title="Copy the current view as an image">
              <Button size="small" icon={<CameraOutlined />} onClick={() => {
                const d = capture(2);
                if (!d) { message.error("Could not read the canvas"); return; }
                const a = document.createElement("a");
                a.href = d; a.download = "model-over-sheet.png"; a.click();
              }} />
            </Tooltip>
          </Space>
          <Button type="primary" size="small" block icon={<SaveOutlined />}
            disabled={!canEdit || !sheetId || !dirty} onClick={save}>
            Save this alignment
          </Button>
          <Tooltip getPopupContainer={popupInStage} title="Put this drawing at the same scale, turn and position on every page, and move the model the same way. Each page keeps its own levels and its own height.">
            <Button size="small" block icon={<SaveOutlined />} data-testid="save-alignment-all"
              disabled={!canEdit || !sheetId || !onSaveOverlay || !onSaveOverlay.saveAll} onClick={saveAll}>
              Save on all levels
            </Button>
          </Tooltip>
          {!canEdit && (
            <div style={{ fontSize: 11, color: T.muted }}>
              Only the project owner can change the alignment - it decides where every status colour lands on the drawing.
            </div>
          )}
        </Space>
        </>)}
      </div>
    </div>
  );
}
